import jsPDF from 'jspdf';

import { mascaraPlaca } from '@/nucleo/formato/documentos';

import type { Comprovante } from './apiDaInscricao';
import { ROTULO_DA_MODALIDADE } from './contrato';

/**
 * O comprovante da inscrição em PDF (RN-CP-27), com todos os protocolos.
 *
 * Gerado no navegador com jsPDF — já dependência do front, e o mesmo caminho do
 * certificado do Portal do Credenciado — a partir do comprovante que a API
 * devolveu: não é uma segunda fonte de dado, é outro formato do mesmo
 * documento. O SHA-256 impresso é o que o banco guardou no envio, calculado
 * sobre o comprovante congelado: é por ele que a SMT confere que o papel
 * apresentado é o que foi enviado.
 *
 * A SMT é a dona do sistema e a emissora do documento.
 */
export function montarComprovantePdf(comprovante: Comprovante): jsPDF {
  const documento = new jsPDF();
  const largura = documento.internal.pageSize.getWidth();
  const altura = documento.internal.pageSize.getHeight();
  const margem = 20;
  let y = 20;

  const quebrarSePreciso = (espaco: number) => {
    if (y + espaco > altura - margem) {
      documento.addPage();
      y = margem;
    }
  };

  documento.setFontSize(10);
  documento.setTextColor(100);
  documento.text('PREFEITURA DO MUNICÍPIO DE SÃO PAULO', largura / 2, y, { align: 'center' });
  y += 5;
  documento.text('SECRETARIA MUNICIPAL DE MOBILIDADE URBANA E TRANSPORTE', largura / 2, y, {
    align: 'center',
  });
  y += 5;
  documento.text('TRANSPORTE ESCOLAR GRATUITO — TEG', largura / 2, y, { align: 'center' });
  y += 12;

  documento.setFontSize(16);
  documento.setTextColor(0);
  documento.text('COMPROVANTE DE INSCRIÇÃO', largura / 2, y, { align: 'center' });
  y += 10;

  documento.setFontSize(10);
  const campo = (rotulo: string, valor: string) => {
    quebrarSePreciso(7);
    documento.setFont('helvetica', 'bold');
    documento.text(`${rotulo}:`, margem, y);
    documento.setFont('helvetica', 'normal');
    const linhas = documento.splitTextToSize(valor, largura - margem - 70) as string[];
    documento.text(linhas, 65, y);
    y += 6 * Math.max(linhas.length, 1);
  };

  campo('Período', comprovante.ciclo.nome);
  campo('Entidade', comprovante.entidade.nome);
  campo('Documento', comprovante.entidade.documento);
  campo('E-mail', comprovante.contatos.email);
  if (comprovante.contatos.telefone) campo('Telefone', comprovante.contatos.telefone);
  campo('Enviada em', new Date(comprovante.enviadoEm).toLocaleString('pt-BR'));
  campo('Edital aceito', `versão ${comprovante.editalVersao}`);
  campo('Aviso de privacidade', `versão ${comprovante.lgpdVersao}`);
  y += 4;

  documento.setFontSize(12);
  documento.setFont('helvetica', 'bold');
  quebrarSePreciso(10);
  documento.text(`Veículos inscritos (${comprovante.veiculos.length})`, margem, y);
  y += 8;
  documento.setFontSize(10);

  for (const veiculo of comprovante.veiculos) {
    quebrarSePreciso(30);
    documento.setFont('helvetica', 'bold');
    documento.text(`Protocolo ${veiculo.protocolo}`, margem, y);
    y += 6;
    documento.setFont('helvetica', 'normal');
    campo('Placa', mascaraPlaca(veiculo.placa));
    campo('Modalidade', ROTULO_DA_MODALIDADE[veiculo.modalidade] ?? veiculo.modalidade);
    campo('DRE', veiculo.dre.nome);
    campo(
      'Escolas',
      veiculo.escolas.map((e) => `${e.prioridade}ª ${e.nome} (EOL ${e.codigoEol})`).join('; '),
    );
    if (veiculo.cooperativa) campo('Cooperativa', veiculo.cooperativa.nome);
    y += 3;
  }

  quebrarSePreciso(20);
  y += 4;
  documento.setFontSize(8);
  documento.setTextColor(90);
  documento.text('Resumo de verificação (SHA-256):', margem, y);
  y += 4;
  documento.text(comprovante.sha256, margem, y);
  y += 5;
  documento.text(`Acompanhe em ${comprovante.linkAcompanhamento}`, margem, y);

  return documento;
}

export function baixarComprovantePdf(comprovante: Comprovante): void {
  const primeiro = comprovante.veiculos[0]?.protocolo ?? 'inscricao';
  montarComprovantePdf(comprovante).save(`comprovante-${primeiro}.pdf`);
}
